import { Info } from "lucide-react";
import { KpiTile } from "@/components/home/KpiTile";
import { AgentsWorking } from "@/components/home/AgentsWorking";
import { TopTopics } from "@/components/home/TopTopics";
import { EngagementChart } from "@/components/home/EngagementChart";
import { MOCK_KPIS } from "@/components/mocks/fixtures";
import { useActiveOrg } from "@/lib/session";
import { useDashboard } from "@/lib/dashboard";
import { formatNumber } from "@/lib/utils";

export function Home() {
  const org = useActiveOrg();
  const { data: dashboard, isLoading } = useDashboard();

  // Fall back to demo fixtures when the org has no real data yet — keeps
  // sales walkthroughs alive against a freshly-provisioned org.
  //
  // IMPORTANT: gate on `has_data`, not on per-field `??`. The backend returns
  // `{ members: 0, weekly_active_pct: 0, ... }` for an empty org, and the
  // `??` operator only falls back on null/undefined — `0` would slip through
  // and the KPI tiles would show real zeros instead of the demo numbers.
  const usingDemo = !dashboard?.has_data;

  const kpis = usingDemo || !dashboard
    ? {
        members: MOCK_KPIS.members,
        members_delta: MOCK_KPIS.membersDelta,
        weekly_active_pct: MOCK_KPIS.weeklyActivePct,
        weekly_active_delta_pp: MOCK_KPIS.weeklyActiveDelta,
        msgs_per_user_per_week: MOCK_KPIS.msgsPerUserPerWeek,
        msgs_delta: MOCK_KPIS.msgsDelta,
        cost_per_user_per_week: MOCK_KPIS.costPerUserPerWeek,
        cost_delta: MOCK_KPIS.costDelta,
      }
    : dashboard.kpis;

  return (
    <div className="anim-fade-up">
      <div className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-slate-300)]">
            {org?.name ?? "Platform"} · Home
          </p>
          <h1 className="mt-2 text-heading-lg font-semibold text-[var(--color-snow)]">
            This week with your members
          </h1>
        </div>
        {usingDemo && !isLoading && <DemoBadge />}
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <KpiTile
          value={formatNumber(kpis.members)}
          label="members"
          delta={{ value: kpis.members_delta }}
          caption="new this week"
        />
        <KpiTile
          value={`${kpis.weekly_active_pct}%`}
          label="weekly active"
          delta={{ value: kpis.weekly_active_delta_pp, suffix: "pp" }}
          caption="vs last week"
        />
        <KpiTile
          value={kpis.msgs_per_user_per_week.toFixed(1)}
          label="msgs / user / wk"
          delta={{ value: kpis.msgs_delta }}
          caption="agent → member"
        />
        <KpiTile
          value={`$${kpis.cost_per_user_per_week.toFixed(2)}`}
          label="$ / user / wk"
          delta={{ value: kpis.cost_delta, inverted: true }}
          caption="agent compute"
        />
      </section>

      <section className="mb-10">
        <AgentsWorking data={dashboard?.has_data ? dashboard.agent_pulse : undefined} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TopTopics data={dashboard?.has_data ? dashboard.top_topics : undefined} />
        <EngagementChart
          data={dashboard?.has_data ? dashboard.engagement_30d : undefined}
        />
      </section>
    </div>
  );
}

function DemoBadge() {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-badge)] shadow-hairline bg-[rgba(86,194,255,0.06)]"
      title="Your cohort hasn't synced real data yet — showing demo data so the page tells a story."
    >
      <Info size={12} className="text-[var(--color-sky)]" />
      <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-slate-200)]">
        Demo data
      </span>
    </div>
  );
}
